import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ROLE_COLORS, ROLE_LABELS } from '../utils/teamMemberDisplay';

const TABS = ['Profile', 'Documents', 'Performance', 'HR Status'];

export default function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Profile');
  const [hr, setHr] = useState<any>({});
  const [savingHr, setSavingHr] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [sendingPayrollInvite, setSendingPayrollInvite] = useState(false);
  const [payrollInviteSent, setPayrollInviteSent] = useState(false);
  const [syncingQbId, setSyncingQbId] = useState(false);
  const [qbSyncResult, setQbSyncResult] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docType, setDocType] = useState('other');
  const [docLabel, setDocLabel] = useState('');

  useEffect(() => {
    api.get(`/employees/${id}`)
      .then(r => {
        setData(r.data);
        setHr({ ...(r.data.record || {}), employment_status: r.data.record?.employment_status === 'terminated' ? 'terminated' : r.data.user.active ? (r.data.record?.employment_status || 'active') : 'inactive' });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const saveHr = async () => {
    setSavingHr(true);
    try {
      const response = await api.patch(`/employees/${id}/hr`, hr);
      setData((prev: any) => ({ ...prev, record: response.data }));
      alert('HR status saved');
    } catch { alert('Failed to save'); }
    finally { setSavingHr(false); }
  };

  const restoreToTeam = async () => {
    if (!window.confirm(`Restore ${data.user.name} to the active Team roster? This enables their CRM account and marks their Employee profile Active.`)) return;
    setRestoring(true);
    try {
      const { data: updatedUser } = await api.patch(`/users/${id}`, { active: true });
      setData((prev: any) => ({ ...prev, user: updatedUser, record: prev.record ? { ...prev.record, employment_status: 'active' } : null }));
      setHr((prev: any) => ({ ...prev, employment_status: 'active' }));
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to restore team access');
    } finally { setRestoring(false); }
  };

  if (loading) return <div style={{ color: '#8FA8C0', padding: 60, textAlign: 'center', background: '#0F1923', minHeight: '100vh' }}>Loading...</div>;
  if (!data) return <div style={{ color: '#EF4444', padding: 60, textAlign: 'center', background: '#0F1923', minHeight: '100vh' }}>Employee not found</div>;

  const { user, record, documents, completions, allModules, performance } = data;
  const roleColor = ROLE_COLORS[user.role] || '#4A7EB5';
  const displayStatus = record?.employment_status === 'terminated' ? 'terminated' : user.active ? (record?.employment_status || 'active') : 'inactive';
  const trainingPct = allModules.length > 0 ? Math.round((completions.length / allModules.length) * 100) : 0;
  const completedIds = new Set(completions.map((c: any) => c.module_id));

  return (
    <div style={{ padding: 24, background: '#0F1923', minHeight: '100%' }}>
      {/* Back */}
      <button onClick={() => navigate('/employees')} style={{ background: 'none', border: 'none', color: '#4A7EB5', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16, padding: 0 }}>
        ← Back to Employees
      </button>

      {/* Header */}
      <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #1E3A5F', padding: 24, marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ width: 52, height: 52, background: roleColor + '22', border: `2px solid ${roleColor}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 900, color: roleColor }}>
              {user.name.charAt(0)}
            </div>
            <div>
              <div style={{ color: '#fff', fontSize: 22, fontWeight: 900 }}>{user.name}</div>
              <div style={{ color: '#4A7EB5', fontSize: 14 }}>{user.email}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Badge label={ROLE_LABELS[user.role] || user.role} color={roleColor} />
            <Badge label={displayStatus} color={displayStatus === 'terminated' ? '#EF4444' : user.active ? '#10B981' : '#6B7280'} />
            {record?.contract_signed && <Badge label="Contract Signed" color="#10B981" />}
            {record?.qb_employee_id && <Badge label={`QB #${record.qb_employee_id}`} color="#8B5CF6" />}
            {record?.work_email_created && <Badge label="Google Account ✓" color="#4A7EB5" />}
          </div>
          {!user.active && (
            <button onClick={restoreToTeam} disabled={restoring}
              style={{ marginTop: 14, background: '#10B98122', color: '#10B981', border: '1px solid #10B98155', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: restoring ? 0.6 : 1 }}>
              {restoring ? 'Restoring...' : 'Restore to Team'}
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <StatBox label="Training" value={`${trainingPct}%`} color={trainingPct === 100 ? '#10B981' : '#F59E0B'} />
          <StatBox label="HR Status" value={record?.background_check_cleared && record?.drug_test_cleared && record?.i9_completed ? '✅' : '⚠️'} color={record?.background_check_cleared ? '#10B981' : '#F59E0B'} />
          <StatBox label="Start Date" value={record?.start_date ? new Date(record.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD'} color="#4A7EB5" />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #1E3A5F', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: 'none', border: 'none',
            color: tab === t ? '#4A7EB5' : '#8FA8C0',
            borderBottom: `2px solid ${tab === t ? '#4A7EB5' : 'transparent'}`,
          }}>{t}</button>
        ))}
      </div>

      {/* Profile Tab */}
      {tab === 'Profile' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card title="Personal Info">
            <Row label="Legal Name" value={record?.legal_name || user.name} />
            <Row label="Personal Email" value={record?.personal_email || '—'} />
            <Row label="Work Email" value={record?.work_email_requested || (user.email.endsWith('@mcmullenbrowne.com') ? user.email : 'Pending')} />
            <Row label="Phone" value={user.phone || record?.phone || '—'} />
            <Row label="Date of Birth" value={record?.date_of_birth ? new Date(record.date_of_birth).toLocaleDateString() : '—'} />
            <Row label="Address" value={[record?.address, record?.city, record?.state, record?.zip].filter(Boolean).join(', ') || '—'} last />
          </Card>
          <Card title="Sizing & Emergency">
            <Row label="Shirt Size" value={record?.shirt_size || '—'} />
            <Row label="Shoe Size" value={record?.shoe_size || '—'} />
            <Row label="Emergency Contact" value={record?.emergency_name || '—'} />
            <Row label="Emergency Phone" value={record?.emergency_phone || '—'} />
            <Row label="Relationship" value={record?.emergency_relationship || '—'} last />
          </Card>
          <Card title="Driver's License">
            <Row label="DL Number" value={record?.dl_number || '—'} />
            <Row label="State" value={record?.dl_state || '—'} />
            <Row label="Expiry" value={record?.dl_expiry ? new Date(record.dl_expiry).toLocaleDateString() : '—'} />
            {record?.dl_photo_url && (
              <div style={{ marginTop: 10 }}>
                <a href={record.dl_photo_url} target="_blank" rel="noreferrer" style={{ color: '#4A7EB5', fontSize: 13, fontWeight: 600 }}>📷 View DL Photo</a>
              </div>
            )}
          </Card>
          <Card title="Payroll">
            <Row label="Pay Rate" value={record?.pay_rate ? `$${record.pay_rate}/hr` : '—'} />
            <Row label="Pay Type" value={record?.pay_type?.replace(/_/g, ' ') || '—'} />
            <Row label="Vehicle Allowance" value={record?.vehicle_allowance ? `$${record.vehicle_allowance_daily}/day` : 'None'} />
            <Row label="Direct Deposit" value={record?.direct_deposit_on_file ? '✅ On file' : '✗ Not on file'} />
            <Row label="QB Employee ID" value={record?.qb_employee_id || '—'} last />
          </Card>
        </div>
      )}

      {/* Documents Tab */}
      {tab === 'Documents' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Upload bar */}
          <div style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 12, padding: '14px 18px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' as const }}>
            <select value={docType} onChange={e => setDocType(e.target.value)}
              style={{ background: '#0F1923', border: '1px solid #2D4259', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}>
              <option value="other">📎 Other</option>
              <option value="w4">📋 W-4</option>
              <option value="i9">🪪 I-9</option>
              <option value="dl_photo">🪪 Driver's License</option>
              <option value="background_check">✅ Background Check</option>
              <option value="drug_test">🧪 Drug Test</option>
              <option value="direct_deposit">🏦 Direct Deposit Form</option>
            </select>
            <input type="text" placeholder="Label (optional)" value={docLabel} onChange={e => setDocLabel(e.target.value)}
              style={{ background: '#0F1923', border: '1px solid #2D4259', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 13, flex: 1, minWidth: 120 }} />
            <label style={{ background: uploadingDoc ? '#2D4259' : '#4A7EB5', color: '#fff', padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: uploadingDoc ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' as const }}>
              {uploadingDoc ? 'Uploading...' : '⬆️ Upload Document'}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display: 'none' }}
                disabled={uploadingDoc}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadingDoc(true);
                  try {
                    const form = new FormData();
                    form.append('file', file);
                    form.append('type', docType);
                    if (docLabel) form.append('label', docLabel);
                    const res = await api.post(`/employees/${id}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
                    setData((prev: any) => ({ ...prev, documents: [res.data.document, ...prev.documents] }));
                    setDocLabel('');
                  } catch { alert('Upload failed. Please try again.'); }
                  finally { setUploadingDoc(false); e.target.value = ''; }
                }}
              />
            </label>
          </div>

          {documents.length === 0 ? (
            <div style={{ color: '#8FA8C0', textAlign: 'center', padding: 40 }}>No documents on file</div>
          ) : documents.map((doc: any) => (
            <div key={doc.id} style={{ background: '#1A2B3C', borderRadius: 12, border: '1px solid #1E3A5F', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                  {doc.type === 'dl_photo' ? '🪪 Driver\'s License' : doc.type === 'contract' ? '📄 Employment Contract' : doc.type === 'w4' ? '📋 W-4' : doc.type === 'i9' ? '🪪 I-9' : doc.type === 'background_check' ? '✅ Background Check' : doc.type === 'drug_test' ? '🧪 Drug Test' : doc.type === 'direct_deposit' ? '🏦 Direct Deposit Form' : `📎 ${doc.filename || doc.type}`}
                </div>
                <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 2 }}>{doc.filename && doc.type !== doc.filename ? doc.filename + ' — ' : ''}Uploaded {new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <a href={doc.url} target="_blank" rel="noreferrer" style={{ background: '#4A7EB5', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700 }}>View →</a>
                <button onClick={async () => {
                  if (!window.confirm('Delete this document?')) return;
                  await api.delete(`/employees/${id}/documents/${doc.id}`);
                  setData((prev: any) => ({ ...prev, documents: prev.documents.filter((d: any) => d.id !== doc.id) }));
                }} style={{ background: '#EF444422', color: '#EF4444', border: '1px solid #EF444444', padding: '8px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>✕</button>
              </div>
            </div>
          ))}
          {record?.contract_pdf_url && !documents.find((d: any) => d.type === 'contract') && (
            <div style={{ background: '#1A2B3C', borderRadius: 12, border: '1px solid #1E3A5F', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>📄 Signed Employment Contract</div>
                <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 2 }}>Signed {record?.contract_signed_at ? new Date(record.contract_signed_at).toLocaleDateString() : ''}</div>
              </div>
              <a href={record.contract_pdf_url} target="_blank" rel="noreferrer" style={{ background: '#4A7EB5', color: '#fff', textDecoration: 'none', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700 }}>View →</a>
            </div>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {tab === 'Performance' && (
        <div>
          {Object.keys(performance).length === 0 ? (
            <div style={{ color: '#8FA8C0', textAlign: 'center', padding: 40 }}>No performance data available for this role</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
              {user.role === 'salesman' && <>
                <PerfStat label="Doors Knocked" value={performance.doors_knocked} icon="🚪" color="#4A7EB5" />
                <PerfStat label="Appointments Set" value={performance.appointments_set} icon="📅" color="#10B981" />
                <PerfStat label="Checklists Submitted" value={performance.checklists_submitted} icon="✅" color="#8B5CF6" />
                <PerfStat label="Conversion Rate" value={`${performance.conversion_rate}%`} icon="📈" color="#F59E0B" />
                <PerfStat label="Commission Earned" value={`$${parseFloat(performance.commission_earned).toLocaleString()}`} icon="💰" color="#10B981" />
                <PerfStat label="Commission Pending" value={`$${parseFloat(performance.commission_pending).toLocaleString()}`} icon="⏳" color="#F59E0B" />
              </>}
              {user.role === 'estimator' && <>
                <PerfStat label="Estimates Created" value={performance.estimates_created} icon="📐" color="#4A7EB5" />
                <PerfStat label="Estimates Signed" value={performance.estimates_signed} icon="✍️" color="#10B981" />
                <PerfStat label="Close Rate" value={`${performance.close_rate}%`} icon="📈" color="#F59E0B" />
                <PerfStat label="Total Revenue" value={`$${parseFloat(performance.total_revenue).toLocaleString()}`} icon="💵" color="#10B981" />
                <PerfStat label="Commission Earned" value={`$${parseFloat(performance.commission_earned).toLocaleString()}`} icon="💰" color="#8B5CF6" />
                <PerfStat label="Avg Job Size" value={`$${parseFloat(performance.avg_job_size).toLocaleString()}`} icon="📊" color="#4A7EB5" />
              </>}
              {user.role === 'foreman' && <>
                <PerfStat label="Jobs Assigned" value={performance.jobs_assigned} icon="🔨" color="#4A7EB5" />
                <PerfStat label="Jobs Completed" value={performance.jobs_completed} icon="✅" color="#10B981" />
                <PerfStat label="Documentation Rate" value={`${performance.documentation_rate}%`} icon="📸" color="#F59E0B" />
              </>}
            </div>
          )}
        </div>
      )}

      {/* HR Status Tab */}
      {tab === 'HR Status' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card title="Clearances">
            <HrCheck label="Background Check Cleared" checked={hr.background_check_cleared} onChange={v => setHr({ ...hr, background_check_cleared: v })} date={hr.background_check_date} onDate={v => setHr({ ...hr, background_check_date: v })} />
            <HrCheck label="Drug Test Cleared" checked={hr.drug_test_cleared} onChange={v => setHr({ ...hr, drug_test_cleared: v })} date={hr.drug_test_date} onDate={v => setHr({ ...hr, drug_test_date: v })} />
            <HrCheck label="I-9 Completed" checked={hr.i9_completed} onChange={v => setHr({ ...hr, i9_completed: v })} date={hr.i9_date} onDate={v => setHr({ ...hr, i9_date: v })} />
          </Card>
          <Card title="Employment">
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Start Date</label>
              <input type="date" value={hr.start_date || ''} onChange={e => setHr({ ...hr, start_date: e.target.value })}
                style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', padding: '9px 12px', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' as const }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', color: '#8FA8C0', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Employment Status</label>
              <select value={user.active ? (hr.employment_status || 'active') : displayStatus} disabled={!user.active} onChange={e => setHr({ ...hr, employment_status: e.target.value })}
                style={{ background: '#243447', border: '1px solid #2D4259', color: '#fff', padding: '9px 12px', borderRadius: 8, fontSize: 14, width: '100%' }}>
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="terminated">Terminated</option>
              </select>
              {!user.active && <div style={{ color: '#8FA8C0', fontSize: 11, marginTop: 5 }}>Restore to Team to reactivate this employee's CRM access.</div>}
            </div>
            <button onClick={saveHr} disabled={savingHr} style={{ background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: savingHr ? 0.6 : 1 }}>
              {savingHr ? 'Saving...' : '💾 Save HR Status'}
            </button>
          </Card>
          <Card title="SMS & Consent">
            <Row label="SMS Consent" value={record?.sms_consent ? `✅ Consented` : '✗ Not consented'} />
            <Row label="Consent Date" value={record?.sms_consent_at ? new Date(record.sms_consent_at).toLocaleDateString() : '—'} last />
          </Card>
          <Card title="System">
            <Row label="CRM Account" value={user.active ? '✅ Active' : '✗ Inactive'} />
            <Row label="Google Account" value={record?.work_email_created ? '✅ Created' : '✗ Not created'} />
            <Row label="QB Payroll ID" value={record?.qb_employee_id || '✗ Not added yet'} />
            <Row label="Work Email" value={record?.work_email_requested || user.email} />
            <Row label="QB Email Sent" value={record?.payroll_invite_sent_at ? `✅ ${new Date(record.payroll_invite_sent_at).toLocaleDateString()}` : '✗ Not sent'} last />

            {/* Sync QB ID from QuickBooks */}
            {!record?.qb_employee_id && (
              <div style={{ marginTop: 14 }}>
                <button
                  onClick={async () => {
                    setSyncingQbId(true);
                    setQbSyncResult(null);
                    try {
                      const res = await api.post(`/employees/${id}/sync-qb-id`);
                      setData((prev: any) => ({ ...prev, record: { ...prev.record, qb_employee_id: res.data.qb_employee_id } }));
                      setQbSyncResult(`✅ Synced! QB ID: ${res.data.qb_employee_id}`);
                    } catch (e: any) {
                      setQbSyncResult(`❌ ${e.response?.data?.error || 'Not found in QB yet'}`);
                    } finally { setSyncingQbId(false); }
                  }}
                  disabled={syncingQbId}
                  style={{ background: '#2CA01C', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: syncingQbId ? 0.6 : 1, width: '100%' }}
                >
                  {syncingQbId ? 'Checking QB...' : '🔄 Sync QB ID'}
                </button>
                {qbSyncResult && <p style={{ fontSize: 12, marginTop: 6, marginBottom: 0, color: qbSyncResult.startsWith('✅') ? '#10B981' : '#EF4444' }}>{qbSyncResult}</p>}
                <p style={{ color: '#8FA8C0', fontSize: 11, marginTop: 4, marginBottom: 0 }}>Click after adding them in QuickBooks — auto-finds by name.</p>
              </div>
            )}

            {/* Resend QB onboarding email */}
            <div style={{ marginTop: 14 }}>
              <button
                onClick={async () => {
                  setSendingPayrollInvite(true);
                  try {
                    await api.post(`/employees/${id}/send-payroll-invite`);
                    setPayrollInviteSent(true);
                    setTimeout(() => setPayrollInviteSent(false), 4000);
                  } catch { alert('Failed to send. Please try again.'); }
                  finally { setSendingPayrollInvite(false); }
                }}
                disabled={sendingPayrollInvite}
                style={{ background: '#2CA01C', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: sendingPayrollInvite ? 0.6 : 1, width: '100%' }}
              >
                {sendingPayrollInvite ? 'Sending...' : payrollInviteSent ? '✅ Sent!' : '📧 Resend QB Onboarding Email'}
              </button>
              <p style={{ color: '#8FA8C0', fontSize: 11, marginTop: 5, marginBottom: 0 }}>Resends the "Add to QB" instructions email to Tyler.</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Badge({ label, color }: { label: string; color: string }) {
  return <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{label}</span>;
}
function StatBox({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ background: '#0F1923', borderRadius: 10, border: '1px solid #1E3A5F', padding: '12px 16px', textAlign: 'center' }}>
      <div style={{ color, fontSize: 20, fontWeight: 800 }}>{value}</div>
      <div style={{ color: '#8FA8C0', fontSize: 11, marginTop: 2 }}>{label}</div>
    </div>
  );
}
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#1A2B3C', borderRadius: 14, border: '1px solid #1E3A5F', padding: 20 }}>
      <div style={{ color: '#4A7EB5', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value, last = false }: { label: string; value: string; last?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: last ? 0 : 10, marginBottom: last ? 0 : 10, borderBottom: last ? 'none' : '1px solid #1E3A5F' }}>
      <span style={{ color: '#8FA8C0', fontSize: 13 }}>{label}</span>
      <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, textAlign: 'right', maxWidth: 220 }}>{value}</span>
    </div>
  );
}
function PerfStat({ label, value, icon, color }: { label: string; value: any; icon: string; color: string }) {
  return (
    <div style={{ background: '#1A2B3C', borderRadius: 12, border: '1px solid #1E3A5F', padding: 18, textAlign: 'center' }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <div style={{ color, fontSize: 26, fontWeight: 900 }}>{value}</div>
      <div style={{ color: '#8FA8C0', fontSize: 12, marginTop: 4 }}>{label}</div>
    </div>
  );
}
function HrCheck({ label, checked, onChange, date, onDate }: { label: string; checked: boolean; onChange: (v: boolean) => void; date: string; onDate: (v: string) => void }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 6 }}>
        <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} style={{ width: 16, height: 16, accentColor: '#4A7EB5' }} />
        <span style={{ color: '#fff', fontSize: 14, fontWeight: checked ? 700 : 400 }}>{label}</span>
        {checked && <span style={{ color: '#10B981', fontSize: 12 }}>✅</span>}
      </label>
      {checked && (
        <input type="date" value={date || ''} onChange={e => onDate(e.target.value)}
          placeholder="Date cleared"
          style={{ background: '#243447', border: '1px solid #2D4259', color: '#8FA8C0', padding: '7px 10px', borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box' as const }} />
      )}
    </div>
  );
}
